import * as Yup from 'yup'

export const getInitialValues = (cbo, ruleSets, providerTypes = [], executiveUser) => {
  return {
    // grant340BInformation
    hrsaId: ruleSets?.grant340BInformation?.hrsaId ?? '',
    hrsaGrantNumber: ruleSets?.grant340BInformation?.hrsaGrantNumber ?? '',
    noticeOfFundingNumber: ruleSets?.grant340BInformation?.noticeOfFundingNumber ?? '',
    hrsaUrl: ruleSets?.grant340BInformation?.hrsaUrl ?? '',
    entityEMRSystem: ruleSets?.grant340BInformation?.entityEMRSystem ?? '',
    grantPatientDefinition: ruleSets?.grant340BInformation?.grantPatientDefinition ?? '',

    // additionalServices
    hepCScreenAndTreatValue: ruleSets?.additionalServices?.hepCScreenAndTreat?.value ?? false,
    hepCScreenAndTreatInfo: ruleSets?.additionalServices?.hepCScreenAndTreat?.info ?? '',

    longitudinalHIVCareValue: ruleSets?.additionalServices?.longitudinalHIVCare?.value ?? false,
    longitudinalHIVCareInfo: ruleSets?.additionalServices?.longitudinalHIVCare?.info ?? '',

    inPersonHealthCenterValue: ruleSets?.additionalServices?.inPersonHealthCenter?.value ?? false,
    inPersonHealthCenterInfo: ruleSets?.additionalServices?.inPersonHealthCenter?.info ?? '',

    labsInPersonValue: ruleSets?.additionalServices?.labsInPerson?.value ?? false,
    labsInPersonInfo: ruleSets?.additionalServices?.labsInPerson?.info ?? '',

    behaviorHealthValue: ruleSets?.additionalServices?.behaviorHealth?.value ?? false,
    behaviorHealthInfo: ruleSets?.additionalServices?.behaviorHealth?.info ?? '',

    mentalHealthValue: ruleSets?.additionalServices?.mentalHealth?.value ?? false,
    mentalHealthInfo: ruleSets?.additionalServices?.mentalHealth?.info ?? '',

    housingValue: ruleSets?.additionalServices?.housing?.value ?? false,
    housingInfo: ruleSets?.additionalServices?.housing?.info ?? '',

    foodInsecurityValue: ruleSets?.additionalServices?.foodInsecurity?.value ?? false,
    foodInsecurityInfo: ruleSets?.additionalServices?.foodInsecurity?.info ?? '',

    // lhcDetails
    participatingInLHC: ruleSets?.lhcDetails?.participatingInLHC ?? false,
    lhcCboLinkageContact: ruleSets?.lhcDetails?.lhcCboLinkageContact ?? '',
    hivCboLinkageContact: ruleSets?.lhcDetails?.hivCboLinkageContact ?? '',
    areaRequests: ruleSets?.lhcDetails?.areaRequests ?? '',
    lhcAdditionalNotes: ruleSets?.lhcDetails?.lhcAdditionalNotes ?? '',

    // patientCare
    stiReporting: ruleSets?.patientCare?.stiReporting ?? '',
    posChlamydia: ruleSets?.patientCare?.posChlamydia ?? '',
    posGonorrhea: ruleSets?.patientCare?.posGonorrhea ?? '',
    posSyphilis: ruleSets?.patientCare?.posSyphilis ?? '',
    posHIV: ruleSets?.patientCare?.posHIV ?? '',

    ...Object.keys(providerTypes).reduce((acc, name) => {
      const providerType = cbo?.cbosProviderTypes?.find((providerType) => providerType.name === name)
      const isCBOProvidesTheType = Boolean(providerType)
      return {
        ...acc,
        [name]: isCBOProvidesTheType,
        [`${name}OnDemand`]: providerType?.onDemand ?? false,
        [`${name}AsyncMessaging`]: providerType?.asyncMessaging ?? false,
        [`${name}Scheduling`]: providerType?.scheduling ?? false,
        [`${name}PreferOnDemand`]: providerType?.preferOnDemand ?? false,
        [`${name}CashPay`]: providerType?.cashPay ?? false,
        [`${name}Glp1`]: providerType?.glp1 ?? false,
      }
    }, {}),

    // financials
    patientTransfers: ruleSets?.financials?.patientTransfers ?? '',
    payorRequiresTransfer: ruleSets?.financials?.payorRequiresTransfer ?? '',
    feeForService: ruleSets?.financials?.feeForService ?? '',

    // contacts
    executiveUser: executiveUser || null,

    contacts: ruleSets?.contacts ?? [],
  }
}

export const validationSchema = Yup.object().shape({
  // grant_340b_information
  hrsaId: Yup.string().trim().required('Required'),
  hrsaGrantNumber: Yup.string().trim().required('Required'),
  noticeOfFundingNumber: Yup.string().trim().required('Required'),
  hrsaUrl: Yup.string().trim().required('Required'),
  entityEMRSystem: Yup.string().trim(),
  grantPatientDefinition: Yup.string().trim(),

  // additional_services
  hepCScreenAndTreatValue: Yup.boolean(),
  hepCScreenAndTreatInfo: Yup.string()
    .trim()
    .when('hepCScreenAndTreatValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  longitudinalHIVCareValue: Yup.boolean(),
  longitudinalHIVCareInfo: Yup.string()
    .trim()
    .when('longitudinalHIVCareValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  inPersonHealthCenterValue: Yup.boolean(),
  inPersonHealthCenterInfo: Yup.string()
    .trim()
    .when('inPersonHealthCenterValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  labsInPersonValue: Yup.boolean(),
  labsInPersonInfo: Yup.string()
    .trim()
    .when('labsInPersonValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  behaviorHealthValue: Yup.boolean(),
  behaviorHealthInfo: Yup.string()
    .trim()
    .when('behaviorHealthValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  mentalHealthValue: Yup.boolean(),
  mentalHealthInfo: Yup.string()
    .trim()
    .when('mentalHealthValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  housingValue: Yup.boolean(),
  housingInfo: Yup.string()
    .trim()
    .when('housingValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  foodInsecurityValue: Yup.boolean(),
  foodInsecurityInfo: Yup.string()
    .trim()
    .when('foodInsecurityValue', {
      is: true,
      then: (schema) => schema.required('Required'),
    }),

  // lhc_details
  participatingInLHC: Yup.boolean(),
  lhcCboLinkageContact: Yup.string().trim(),
  hivCboLinkageContact: Yup.string().trim(),
  areaRequests: Yup.string().trim(),
  lhcAdditionalNotes: Yup.string().trim(),

  // patient_care
  stiReporting: Yup.string().required('Required'),
  posChlamydia: Yup.string().required('Required'),
  posGonorrhea: Yup.string().required('Required'),
  posSyphilis: Yup.string().required('Required'),
  posHIV: Yup.string().required('Required'),

  // financials
  patientTransfers: Yup.string().required('Required'),
  payorRequiresTransfer: Yup.string().required('Required'),
  feeForService: Yup.string().required('Required'),

  // contacts
  executiveUser: Yup.object().required('Required').nullable(),

  contacts: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().trim().required('Name is required'),
      email: Yup.string().trim().email('A valid email address is required'),
      title: Yup.string().trim(),
    })
  ),
})

export const formikToApiData = (data) => {
  return {
    grant_340b_information: JSON.stringify({
      hrsaId: data.hrsaId,
      hrsaGrantNumber: data.hrsaGrantNumber,
      noticeOfFundingNumber: data.noticeOfFundingNumber,
      hrsaUrl: data.hrsaUrl,
      entityEMRSystem: data.entityEMRSystem,
      grantPatientDefinition: data.grantPatientDefinition,
    }),
    additional_services: JSON.stringify({
      hepCScreenAndTreat: { value: data.hepCScreenAndTreatValue, info: data.hepCScreenAndTreatInfo },
      longitudinalHIVCare: { value: data.longitudinalHIVCareValue, info: data.longitudinalHIVCareInfo },
      inPersonHealthCenter: { value: data.inPersonHealthCenterValue, info: data.inPersonHealthCenterInfo },
      labsInPerson: { value: data.labsInPersonValue, info: data.labsInPersonInfo },
      behaviorHealth: { value: data.behaviorHealthValue, info: data.behaviorHealthInfo },
      mentalHealth: { value: data.mentalHealthValue, info: data.mentalHealthInfo },
      housing: { value: data.housingValue, info: data.housingInfo },
      foodInsecurity: { value: data.foodInsecurityValue, info: data.foodInsecurityInfo },
    }),
    lhc_details: JSON.stringify({
      participatingInLHC: data.participatingInLHC,
      lhcCboLinkageContact: data.lhcCboLinkageContact,
      hivCboLinkageContact: data.hivCboLinkageContact,
      areaRequests: data.areaRequests,
      lhcAdditionalNotes: data.lhcAdditionalNotes,
    }),
    patient_care: JSON.stringify({
      stiReporting: data.stiReporting,
      posChlamydia: data.posChlamydia,
      posGonorrhea: data.posGonorrhea,
      posSyphilis: data.posSyphilis,
      posHIV: data.posHIV,
    }),
    financials: JSON.stringify({
      patientTransfers: data.patientTransfers,
      payorRequiresTransfer: data.payorRequiresTransfer,
      feeForService: data.feeForService,
    }),
    executive_user_id: data.executiveUser.id,
    contacts: JSON.stringify(data.contacts),
  }
}
